export interface CreatedDateFilterState {
  /** ISO date `YYYY-MM-DD` */
  from: string;
  /** ISO date `YYYY-MM-DD` */
  to: string;
}

export function emptyCreatedDateFilter(): CreatedDateFilterState {
  return { from: "", to: "" };
}

/** Today as `YYYY-MM-DD` (local) — use as a date input `max` to block future dates. */
export function todayIsoDate(): string {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/** Default range: 7 days ago through today (`YYYY-MM-DD`). */
export function getDefaultCreatedDateRange(): CreatedDateFilterState {
  const today = new Date();
  const oneWeekAgo = new Date(today);
  oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);

  const formatDate = (d: Date) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  return {
    from: formatDate(oneWeekAgo),
    to: formatDate(today),
  };
}

function normalizeDateValue(raw: unknown): string {
  if (typeof raw !== "string") return "";
  return raw.trim();
}

export function normalizeCreatedDateFilter(
  state: Partial<CreatedDateFilterState> & { date?: string } | null | undefined
): CreatedDateFilterState {
  if (!state) return emptyCreatedDateFilter();
  const legacyDate = normalizeDateValue(state.date);
  const from = normalizeDateValue(state.from) || legacyDate;
  const to = normalizeDateValue(state.to) || legacyDate;
  return { from, to };
}

function parseFilterDate(isoDate: string): Date | undefined {
  const trimmed = normalizeDateValue(isoDate);
  if (!trimmed) return undefined;
  const d = new Date(`${trimmed}T00:00:00`);
  return Number.isNaN(d.getTime()) ? undefined : d;
}

function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function endOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
}

export function resolveCreatedDateRange(
  state: Partial<CreatedDateFilterState> & { date?: string }
): { from?: Date; to?: Date } {
  const normalized = normalizeCreatedDateFilter(state);
  let fromDay = parseFilterDate(normalized.from);
  let toDay = parseFilterDate(normalized.to);

  if (!fromDay && !toDay) return {};

  if (fromDay && toDay && fromDay.getTime() > toDay.getTime()) {
    const swap = fromDay;
    fromDay = toDay;
    toDay = swap;
  }

  return {
    from: fromDay ? startOfDay(fromDay) : undefined,
    to: toDay ? endOfDay(toDay) : fromDay ? endOfDay(fromDay) : undefined,
  };
}

/** Query params for ticket list APIs (`created_on` field). */
export function createdDateRangeToApiParams(
  state: Partial<CreatedDateFilterState> & { date?: string }
): Record<string, string> {
  const { from, to } = resolveCreatedDateRange(state);
  const params: Record<string, string> = {};
  if (from) params.created_on__gte = from.toISOString();
  if (to) params.created_on__lte = to.toISOString();
  return params;
}

export function ticketCreatedAtInRange(
  createdAtIso: string | undefined,
  state: Partial<CreatedDateFilterState> & { date?: string }
): boolean {
  const normalized = normalizeCreatedDateFilter(state);
  if (!normalized.from && !normalized.to) return true;

  const { from, to } = resolveCreatedDateRange(normalized);
  if (!from && !to) return true;
  if (!createdAtIso) return false;

  const t = new Date(createdAtIso).getTime();
  if (Number.isNaN(t)) return false;
  if (from && t < from.getTime()) return false;
  if (to && t > to.getTime()) return false;
  return true;
}

/** `YYYY-MM-DD` → `DD-MM-YYYY` for filter display. */
export function formatCreatedDateDisplay(isoDate: string): string {
  const trimmed = normalizeDateValue(isoDate);
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed);
  if (!match) return "";
  return `${match[3]}-${match[2]}-${match[1]}`;
}

export function hasCreatedDateFilter(
  state: Partial<CreatedDateFilterState> & { date?: string }
): boolean {
  const normalized = normalizeCreatedDateFilter(state);
  return Boolean(normalized.from || normalized.to);
}
